/**
 * Athena Workspace — Layout Renderers
 * Correção 3 Camada 3 · Camada 6.2
 *
 * Camada 6.2: cada renderer retorna um Fragment com:
 *   NarrativeBlock(intro) → Card principal → NarrativeBlock(afterText) → FollowUpText
 * Conteúdo do card nunca se repete fora do card.
 */

import React from 'react';
import {
  PresentationCard, PresentationSection, HighlightBox,
  KeyValueGrid, StepList, ComparisonBlock, ActionBar,
  NarrativeBlock, FollowUpText,
  PT,
} from './components';
import {
  parseNarrativeBlocks, extractSteps, extractBullets,
  extractKeyValues, classifySectionContent, sectionIs,
  stripBold,
} from './parser';

// ── Shared: render a section's content adaptively ───────────────────────────
function SectionContent({ content }) {
  if (!content) return null;
  const type = classifySectionContent(content);

  if (type === 'steps') {
    return <StepList steps={extractSteps(content)} />;
  }
  if (type === 'bullets') {
    const items = extractBullets(content);
    return (
      <ul style={{ margin: 0, padding: '0 0 0 16px', listStyle: 'none' }}>
        {items.map((item, i) => (
          <li key={i} style={{
            display: 'flex', gap: 8, marginBottom: 5,
            fontSize: PT.fBase, color: PT.textSec, lineHeight: 1.55,
          }}>
            <span style={{ color: PT.accent, flexShrink: 0, marginTop: 2 }}>·</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (type === 'keyvalue') {
    const kv = extractKeyValues(content);
    if (kv) return <KeyValueGrid items={kv} />;
  }
  return (
    <div style={{ fontSize: PT.fBase, color: PT.textSec, lineHeight: 1.68, whiteSpace: 'pre-wrap' }}>
      {stripBold(content)}
    </div>
  );
}

// ── Fallback de texto dentro do card (sem seções estruturadas) ───────────────
function IntroText({ text, limit = 280 }) {
  if (!text) return null;
  const t = stripBold(text).trim();
  if (!t) return null;
  const shown = t.length > limit ? t.slice(0, limit).trimEnd() + '…' : t;
  return (
    <div style={{
      fontSize: PT.fBase, color: PT.textSec,
      lineHeight: 1.65, marginBottom: 14,
    }}>
      {shown}
    </div>
  );
}

// ── Recipe helpers ────────────────────────────────────────────────────────────
const R = {
  card:       '#0c1220',
  border:     '#1a2240',
  accent:     '#3B82F6',
  accentSoft: 'rgba(59,130,246,0.12)',
  accentBdr:  'rgba(59,130,246,0.22)',
  text:       '#e8eaf2',
  textSec:    'rgba(220,234,255,0.78)',
  textMut:    'rgba(150,170,220,0.6)',
  green:      '#22c55e',
  greenSoft:  'rgba(34,197,94,0.12)',
  greenBdr:   'rgba(34,197,94,0.22)',
  purple:     '#a78bfa',
  purpleSoft: 'rgba(139,92,246,0.1)',
  purpleBdr:  'rgba(139,92,246,0.22)',
  indigo:     '#818cf8',
  indigoBdr:  'rgba(99,102,241,0.3)',
  amber:      '#f59e0b',
  amberSoft:  'rgba(245,158,11,0.08)',
  amberBdr:   'rgba(245,158,11,0.25)',
};

const _rCard = {
  background:   R.card,
  border:       `1px solid ${R.border}`,
  borderRadius: 11,
  padding:      '8px 11px',
  boxShadow:    '0 2px 10px rgba(0,0,0,0.2)',
};

const _rLabel = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: 10, fontWeight: 700, color: R.textMut,
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 7,
};

function useIsMobile(bp = 700) {
  const [mobile, setMobile] = React.useState(
    () => typeof window !== 'undefined' ? window.innerWidth < bp : false
  );
  React.useEffect(() => {
    const fn = () => setMobile(window.innerWidth < bp);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, [bp]);
  return mobile;
}

function RecipeSectionLabel({ icon, label }) {
  return (
    <div style={_rLabel}>
      <span style={{ fontSize: 14 }}>{icon}</span>
      <span>{label}</span>
    </div>
  );
}

// Stat block: icon+value on top, label below — matches Image 1 & Image 4
function StatBlock({ icon, value, label, flex1 }) {
  if (!value) return null;
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
      padding: '6px 8px',
      border: `1px solid ${R.border}`,
      borderRadius: 9,
      background: 'rgba(255,255,255,0.025)',
      flex: flex1 ? 1 : undefined,
      minWidth: flex1 ? 0 : undefined,
    }}>
      <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:12.5, color:R.text, fontWeight:600, whiteSpace:'nowrap' }}>
        <span>{icon}</span><span>{value}</span>
      </div>
      <div style={{ fontSize:9.5, color:R.textMut, whiteSpace:'nowrap' }}>{label}</div>
    </div>
  );
}

function _findKV(items, ...keys) {
  return items?.find(kv => keys.some(k => kv.key.toLowerCase().includes(k)))?.value;
}

// ── 1. RecipePresentation ─────────────────────────────────────────────────────
export function RecipePresentation({ content, title, meta }) {
  const isMobile = useIsMobile();
  const [stepsExpanded, setStepsExpanded] = React.useState(false);
  const [recipeImgUrl, setRecipeImgUrl] = React.useState(null);
  const { intro, sections, afterText, followUp } = parseNarrativeBlocks(content);

  const ingredSection = sections.find(s => sectionIs(s.title, 'ingred'));
  const prepSection   = sections.find(s => sectionIs(s.title, 'preparo', 'modo', 'prepara', 'instruç'));
  const dicaSection   = sections.find(s => sectionIs(s.title, 'dica', 'observ', 'nota', 'atenç', 'lembre'));
  const otherSections = sections.filter(s => s !== ingredSection && s !== prepSection && s !== dicaSection);

  const recipeName    = title || meta?.title || 'Receita';
  const recipeInitial = recipeName.replace(/[^a-zA-ZÀ-ú]/g, '')[0]?.toUpperCase() || 'R';
  // Tamanho adaptativo do título — títulos longos usam fonte menor
  const titleFs = recipeName.length > 40 ? 16 : recipeName.length > 28 ? 18 : 21;

  // Busca imagem real da receita via TheMealDB (free, no auth)
  React.useEffect(() => {
    const stopWords = new Set(['de','do','da','com','ao','e','em','para','uma','um','no','na','receita','os','as','dos','das','ao','à','com']);
    const keywords = recipeName.split(/\s+/).filter(w => w.length > 2 && !stopWords.has(w.toLowerCase())).slice(0, 3).join(' ');
    if (!keywords) return;
    fetch(`https://www.themealdb.com/api/json/v1/1/search.php?s=${encodeURIComponent(keywords)}`)
      .then(r => r.json())
      .then(d => { if (d?.meals?.[0]?.strMealThumb) setRecipeImgUrl(d.meals[0].strMealThumb); })
      .catch(() => {});
  }, [recipeName]);

  // Extrai TODOS os pares **Chave:** Valor E "Chave: Valor" de todo o texto
  const _allKvSrc = [intro, ...sections.map(s => s.content)].filter(Boolean);
  const kvItems = _allKvSrc.flatMap(src =>
    src.split('\n').filter(l => l.trim()).flatMap(line => {
      const m1 = line.match(/\*\*([^*]{2,30})\*\*[:\s–-]+(.{2,80})/);
      const m2 = !m1 && line.match(/^([A-ZÀ-Ú][^:\n*]{2,28}):\s+(.{2,60})$/);
      const m  = m1 || m2;
      return m ? [{ key: m[1].replace(/:+$/, '').trim(), value: stripBold(m[2].split(/[.!]/)[0].trim()) }] : [];
    })
  );
  // Blocker 2: validadores — descartam valores capturados por engano
  // (ex.: "Modo de Preparo" → "1. Preaqueça o forno..." virando tempo "1")
  const _validTime = v => v && /\d/.test(v) && v.length <= 25;
  const _validDiff = v => v && /^(fácil|facil|médio|medio|difícil|dificil|simples|moderado|easy|medium|hard)/i.test(v.trim());
  const _validServ = v => v && /\d/.test(v) && v.length <= 20;

  const _timeRaw  = _findKV(kvItems, 'tempo total', 'tempo de preparo', 'tempo', 'time', 'minuto', 'hora', 'duração', 'duracao');
  const _diffRaw  = _findKV(kvItems, 'dific', 'nível', 'nivel', 'grau');
  const _portRaw  = _findKV(kvItems, 'porç', 'porc', 'rend', 'quant', 'pessoa', 'serviço', 'servings');
  const timeVal   = _validTime(_timeRaw) ? _timeRaw : null;
  const diffVal   = _validDiff(_diffRaw) ? _diffRaw : null;
  const portVal   = _validServ(_portRaw) ? _portRaw : null;
  const perfilVal = _findKV(kvItems, 'perfil', 'tipo', 'estilo') || 'Equilibrado';

  const descText = (intro || '').split('\n')
    .map(l => stripBold(l.trim()))
    .find(l => l.length > 28 && !l.startsWith('#') && !l.match(/^\*\*[^*]+\*\*[:\s]/)
      && l.toLowerCase().trim() !== recipeName.toLowerCase().trim()) || '';

  // Se não há seção de ingredientes explícita, tenta extrair do intro (modelo 8B omite o cabeçalho)
  const ingredients = ingredSection
    ? (extractBullets(ingredSection.content) || [])
    : (extractBullets(intro) || []);
  const steps = prepSection ? (extractSteps(prepSection.content) || []) : [];
  const tip   = dicaSection ? stripBold(dicaSection.content).trim()      : null;

  const hasStructured = sections.length > 0;

  // Bottom follow-up chips (Image 1 style)
  const followChips = [
    { label: 'Versão Air Fryer', icon: '🍟' },
    { label: 'Versão Fitness',   icon: '💪' },
    { label: 'Sem Lactose',      icon: '🥛' },
    { label: 'Mais porções',     icon: '👥' },
  ];

  const actionBtns = [
    { id: 'save',  icon: '🔖', label: 'Salvar receita'  },
    { id: 'copy',  icon: '📋', label: 'Copiar receita'  },
    { id: 'adapt', icon: '🔄', label: 'Adaptar receita' },
    { id: 'image', icon: '🖼️', label: 'Gerar imagem'   },
    { id: 'share', icon: '🔗', label: 'Compartilhar'    },
  ];

  // Sidebar adapt pills (Image 1: text-only pills, no icons)
  const adaptPills = ['Low Carb', 'Fitness', 'Sem Lactose', 'Vegetariana', 'Mais Proteica'];

  if (!hasStructured) {
    return (
      <>
        {intro && <NarrativeBlock text={intro} />}
        <PresentationCard title={recipeName} icon="🍽" variant="default">
          <IntroText text={content} limit={800} />
        </PresentationCard>
      </>
    );
  }

  // ── Tip inner JSX (reused on desktop inside card, standalone on mobile) ──
  const TipInner = () => (
    <div style={{ display:'flex', gap:8 }}>
      <span style={{ fontSize:15, flexShrink:0, lineHeight:1.3 }}>💡</span>
      <div>
        <div style={{ fontSize:9.5, fontWeight:700, color:R.amber, marginBottom:2, textTransform:'uppercase', letterSpacing:'0.07em' }}>
          Dica da Athena
        </div>
        <div style={{ fontSize:12, color:'rgba(255,228,140,0.85)', lineHeight:1.5 }}>{tip}</div>
      </div>
    </div>
  );

  // ── Right sidebar (same on desktop; stacked at bottom on mobile) ──
  const SidebarCards = () => (
    <>
      {/* Resumo Rápido */}
      <div style={_rCard}>
        <RecipeSectionLabel icon="📋" label="Resumo Rápido" />
        <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {[
            { icon:'⏱', k:'Tempo total',  v: timeVal || '—'       },
            { icon:'📊', k:'Dificuldade',  v: diffVal || '—'       },
            { icon:'👥', k:'Rendimento',   v: portVal || '—'       },
            { icon:'🍽', k:'Tipo',         v: 'Prato principal'    },
          ].map(({ icon, k, v }) => (
            <div key={k} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:11.5 }}>
              <span style={{ color:R.textMut, display:'flex', alignItems:'center', gap:4 }}>
                <span>{icon}</span><span>{k}</span>
              </span>
              <span style={{ color:R.text, fontWeight:500 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Deseja adaptar? */}
      <div style={_rCard}>
        <RecipeSectionLabel icon="🔄" label="Deseja adaptar?" />
        <div style={{ fontSize:11, color:R.textMut, marginBottom:7, lineHeight:1.4 }}>
          Posso adaptar essa receita para:
        </div>
        <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
          {adaptPills.map(opt => (
            <div key={opt} style={{
              padding:'4px 10px', borderRadius:20, cursor:'pointer',
              background:R.accentSoft, border:`1px solid ${R.accentBdr}`,
              fontSize:11, color:'rgba(180,210,255,0.82)',
            }}>{opt}</div>
          ))}
        </div>
      </div>

      {/* Ações */}
      <div style={_rCard}>
        <RecipeSectionLabel icon="⚡" label="Ações" />
        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {actionBtns.map(a => (
            <button key={a.id} onClick={() => {
              if (a.id === 'copy') navigator.clipboard?.writeText(content);
            }} style={{
              display:'flex', alignItems:'center', gap:8,
              padding:'7px 10px', borderRadius:7, cursor:'pointer',
              background:'rgba(255,255,255,0.025)',
              border:'1px solid rgba(255,255,255,0.07)',
              color:R.textSec, fontSize:12,
              textAlign:'left', width:'100%',
            }}>
              <span style={{ fontSize:14 }}>{a.icon}</span>
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );

  // ══════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT  (Image 4)
  // ══════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>

        {/* Hero card — text LEFT, photo RIGHT, stat row at bottom */}
        <div style={{ ..._rCard, padding:0, overflow:'hidden' }}>
          {/* Top section: text + photo side by side */}
          <div style={{ display:'flex', minHeight:148 }}>
            {/* Text side */}
            <div style={{ flex:'1 1 0', padding:'14px 16px', display:'flex', flexDirection:'column', justifyContent:'center', gap:7, minWidth:0 }}>
              <div style={{
                alignSelf:'flex-start',
                padding:'3px 10px', borderRadius:20,
                background:'rgba(139,92,246,0.18)', border:`1px solid ${R.purpleBdr}`,
                fontSize:9, fontWeight:700, color:R.purple, letterSpacing:'0.1em', textTransform:'uppercase',
              }}>RECEITA</div>
              <div style={{ fontSize:19, fontWeight:700, color:R.text, lineHeight:1.2 }}>
                {recipeName}
              </div>
              {descText && (
                <div style={{ fontSize:12.5, color:R.textSec, lineHeight:1.5 }}>
                  {descText}
                </div>
              )}
            </div>
            {/* Photo side */}
            <div style={{
              flex:'0 0 42%',
              background:'linear-gradient(135deg, #080f1e 0%, #0d1830 55%, #0a142a 100%)',
              display:'flex', flexDirection:'column',
              alignItems:'center', justifyContent:'center',
              gap:4, position:'relative', overflow:'hidden',
              borderLeft:`1px solid ${R.border}`,
            }}>
              <div style={{ position:'absolute', top:-24, right:-24, width:80, height:80, borderRadius:'50%', background:'rgba(59,130,246,0.08)' }} />
              <div style={{ position:'absolute', bottom:-16, left:-16, width:60, height:60, borderRadius:'50%', background:'rgba(139,92,246,0.06)' }} />
              <div style={{
                fontSize:46, fontWeight:800, color:'rgba(59,130,246,0.24)',
                fontFamily:'Georgia,serif', lineHeight:1, zIndex:1, userSelect:'none',
              }}>{recipeInitial}</div>
              <div style={{ fontSize:9, color:'rgba(100,140,210,0.4)', zIndex:1 }}>foto em breve</div>
            </div>
          </div>

          {/* Stat row — sempre 4 itens */}
          <div style={{
            borderTop:`1px solid ${R.border}`,
            display:'flex', padding:'7px 10px', gap:5,
          }}>
            <StatBlock icon="⏱" value={timeVal || '—'}  label="Tempo total" flex1 />
            <StatBlock icon="📊" value={diffVal || '—'}  label="Dificuldade" flex1 />
            <StatBlock icon="👥" value={portVal || '—'}  label="Rendimento"  flex1 />
            <StatBlock icon="🥗" value={perfilVal}        label="Perfil"      flex1 />
          </div>
        </div>

        {/* Ingredientes */}
        {ingredients.length > 0 && (
          <div style={_rCard}>
            <RecipeSectionLabel icon="🌿" label="Ingredientes" />
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {ingredients.map((ing, i) => (
                <div key={i} style={{ display:'flex', gap:10, fontSize:13.5, color:R.textSec, lineHeight:1.5 }}>
                  <div style={{
                    flexShrink:0, width:20, height:20, borderRadius:'50%',
                    background:R.greenSoft, border:`1.5px solid ${R.greenBdr}`,
                    color:R.green, fontSize:11, fontWeight:700,
                    display:'flex', alignItems:'center', justifyContent:'center', marginTop:1,
                  }}>✓</div>
                  <span>{ing}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dica da Athena — standalone amber card */}
        {tip && (
          <div style={{ ..._rCard, background:R.amberSoft, border:`1px solid ${R.amberBdr}` }}>
            <TipInner />
          </div>
        )}

        {/* Modo de Preparo — horizontal carousel + expandable */}
        {steps.length > 0 && (
          <div style={_rCard}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
              <div style={_rLabel}>
                <span style={{ fontSize:14 }}>🍳</span>
                <span>MODO DE PREPARO</span>
              </div>
              <button onClick={() => setStepsExpanded(e => !e)} style={{
                fontSize:11.5, color:R.accent,
                background:'rgba(59,130,246,0.1)', border:`1px solid ${R.accentBdr}`,
                borderRadius:20, padding:'4px 12px', cursor:'pointer', whiteSpace:'nowrap', flexShrink:0,
              }}>
                {stepsExpanded ? 'Recolher ↑' : 'Ver todos os passos ↓'}
              </button>
            </div>

            {stepsExpanded ? (
              <div style={{ display:'flex', flexDirection:'column', gap:13 }}>
                {steps.map((step, i) => (
                  <div key={i} style={{ display:'flex', gap:12, fontSize:13.5, color:R.textSec, lineHeight:1.6 }}>
                    <div style={{
                      flexShrink:0, width:24, height:24, borderRadius:'50%',
                      background:'rgba(99,102,241,0.14)', border:`1.5px solid ${R.indigoBdr}`,
                      color:R.indigo, fontSize:12, fontWeight:700,
                      display:'flex', alignItems:'center', justifyContent:'center', marginTop:1,
                    }}>{i+1}</div>
                    <span>{step}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                display:'flex', overflowX:'auto', gap:9, paddingBottom:4,
                scrollbarWidth:'none', msOverflowStyle:'none',
              }}>
                {steps.map((step, i) => (
                  <div key={i} style={{
                    flexShrink:0, width:118,
                    display:'flex', flexDirection:'column', alignItems:'center', gap:9,
                    padding:'12px 10px',
                    background:'rgba(255,255,255,0.025)',
                    border:`1px solid ${R.border}`,
                    borderRadius:10,
                  }}>
                    <div style={{
                      width:28, height:28, borderRadius:'50%',
                      background:'rgba(99,102,241,0.14)', border:`1.5px solid ${R.indigoBdr}`,
                      color:R.indigo, fontSize:12, fontWeight:700, flexShrink:0,
                      display:'flex', alignItems:'center', justifyContent:'center',
                    }}>{i+1}</div>
                    <div style={{ fontSize:11.5, color:R.textSec, lineHeight:1.5, textAlign:'center' }}>
                      {step.length > 80 ? step.slice(0, 80) + '…' : step}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Other sections */}
        {otherSections.map((s, i) => (
          <div key={i} style={_rCard}>
            <RecipeSectionLabel icon="·" label={s.title} />
            <SectionContent content={s.content} />
          </div>
        ))}

        {/* Follow-up */}
        {(afterText || followUp) && (
          <div>
            {afterText && <NarrativeBlock text={afterText} />}
            {followUp  && <FollowUpText   text={followUp}  />}
          </div>
        )}

        {/* Chips de ação follow-up */}
        <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
          {followChips.map(({ label, icon }) => (
            <div key={label} style={{
              display:'inline-flex', alignItems:'center', gap:6,
              padding:'8px 15px', borderRadius:20, cursor:'pointer',
              background:R.accentSoft, border:`1px solid ${R.accentBdr}`,
              fontSize:13, color:'rgba(180,210,255,0.85)',
            }}>
              <span>{icon}</span><span>{label}</span>
            </div>
          ))}
        </div>

        {/* Sidebar ao final */}
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          <SidebarCards />
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT  (Image 1)
  // ══════════════════════════════════════════════════════════════════
  return (
    <div style={{ display:'flex', gap:6, alignItems:'flex-start' }}>

      {/* Main column */}
      <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:6 }}>

        {/* Hero card: photo LEFT (44%), text+stats RIGHT (56%) — altura fixa panorâmica */}
        <div style={{ ..._rCard, padding:0, overflow:'hidden', display:'flex', height:155 }}>
          {/* Photo area — real image or placeholder */}
          <div style={{ flex:'0 0 42%', position:'relative', overflow:'hidden', borderRight:`1px solid ${R.border}` }}>
            {/* RECEITA badge sempre visível */}
            <div style={{
              position:'absolute', top:8, left:10, zIndex:3,
              padding:'2px 8px', borderRadius:20,
              background: recipeImgUrl ? 'rgba(100,50,220,0.82)' : 'rgba(139,92,246,0.18)',
              border:`1px solid ${R.purpleBdr}`,
              fontSize:8, fontWeight:700, color: recipeImgUrl ? '#fff' : R.purple,
              letterSpacing:'0.1em', textTransform:'uppercase',
              backdropFilter: recipeImgUrl ? 'blur(6px)' : 'none',
            }}>RECEITA</div>

            {recipeImgUrl ? (
              <img src={recipeImgUrl} alt={recipeName}
                style={{ width:'100%', height:'100%', objectFit:'cover', display:'block' }} />
            ) : (
              <div style={{
                width:'100%', height:'100%',
                background:'linear-gradient(145deg, #060d1c 0%, #0b1428 55%, #081020 100%)',
                display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:4,
              }}>
                <div style={{ position:'absolute', top:-18, right:-18, width:70, height:70, borderRadius:'50%', background:'rgba(59,130,246,0.09)' }} />
                <div style={{ position:'absolute', bottom:-12, left:-12, width:55, height:55, borderRadius:'50%', background:'rgba(139,92,246,0.07)' }} />
                <div style={{ fontSize:44, fontWeight:800, color:'rgba(59,130,246,0.18)', fontFamily:'Georgia,serif', lineHeight:1, userSelect:'none', zIndex:1 }}>
                  {recipeInitial}
                </div>
                <div style={{ fontSize:8, color:'rgba(100,140,210,0.3)', zIndex:1 }}>foto em breve</div>
              </div>
            )}
          </div>

          {/* Text + stat blocks */}
          <div style={{ flex:1, padding:'12px 16px', display:'flex', flexDirection:'column', justifyContent:'space-between', overflow:'hidden' }}>
            <div>
              {/* Título com clamp 2 linhas */}
              <div style={{
                fontSize: titleFs, fontWeight:700, color:R.text, lineHeight:1.22, marginBottom:5,
                display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden',
              }}>{recipeName}</div>
              {descText && (
                <div style={{
                  fontSize:11.5, color:R.textSec, lineHeight:1.38, marginBottom:0,
                  display:'-webkit-box', WebkitLineClamp:1, WebkitBoxOrient:'vertical', overflow:'hidden',
                }}>{descText}</div>
              )}
            </div>
            {/* Stat blocks — sempre 4 */}
            <div style={{ display:'flex', gap:5, flexWrap:'nowrap' }}>
              <StatBlock icon="⏱" value={timeVal || '—'}  label="Tempo total" flex1 />
              <StatBlock icon="📊" value={diffVal || '—'}  label="Dificuldade" flex1 />
              <StatBlock icon="👥" value={portVal || '—'}  label="Rendimento"  flex1 />
              <StatBlock icon="🥗" value={perfilVal}        label="Perfil"      flex1 />
            </div>
          </div>
        </div>

        {/* 2-column: Ingredients (left) + Prep (right) */}
        {(ingredients.length > 0 || steps.length > 0) && (
          <div style={{ display:'flex', gap:6 }}>

            {/* Left: Ingredients + Dica inside */}
            {ingredients.length > 0 && (
              <div style={{ ..._rCard, flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:6 }}>
                <RecipeSectionLabel icon="🌿" label="Ingredientes" />
                <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:200, overflowY:'auto' }}>
                  {ingredients.map((ing, i) => (
                    <div key={i} style={{ display:'flex', gap:8, fontSize:12.5, color:R.textSec, lineHeight:1.38 }}>
                      <div style={{
                        flexShrink:0, width:16, height:16, borderRadius:'50%',
                        background:R.greenSoft, border:`1.5px solid ${R.greenBdr}`,
                        color:R.green, fontSize:9, fontWeight:700,
                        display:'flex', alignItems:'center', justifyContent:'center', marginTop:1,
                      }}>✓</div>
                      <span>{ing}</span>
                    </div>
                  ))}
                </div>
                {/* Dica da Athena inside ingredients card */}
                {tip && (
                  <div style={{
                    marginTop:2,
                    background:R.amberSoft, border:`1px solid ${R.amberBdr}`,
                    borderRadius:8, padding:'7px 10px',
                  }}>
                    <TipInner />
                  </div>
                )}
              </div>
            )}

            {/* Right: Preparation steps */}
            {steps.length > 0 && (
              <div style={{ ..._rCard, flex:1, minWidth:0 }}>
                <RecipeSectionLabel icon="🍳" label="Modo de Preparo" />
                <div style={{ display:'flex', flexDirection:'column', gap:6, maxHeight:240, overflowY:'auto' }}>
                  {steps.map((step, i) => (
                    <div key={i} style={{ display:'flex', gap:9, fontSize:12.5, color:R.textSec, lineHeight:1.4 }}>
                      <div style={{
                        flexShrink:0, width:20, height:20, borderRadius:'50%',
                        background:'rgba(99,102,241,0.12)', border:`1.5px solid ${R.indigoBdr}`,
                        color:R.indigo, fontSize:10.5, fontWeight:700,
                        display:'flex', alignItems:'center', justifyContent:'center', marginTop:1,
                      }}>{i+1}</div>
                      <span>{step}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tip standalone if no ingredients column */}
        {tip && ingredients.length === 0 && (
          <div style={{ ..._rCard, background:R.amberSoft, border:`1px solid ${R.amberBdr}` }}>
            <TipInner />
          </div>
        )}

        {/* Other sections */}
        {otherSections.map((s, i) => (
          <div key={i} style={_rCard}>
            <RecipeSectionLabel icon="·" label={s.title} />
            <SectionContent content={s.content} />
          </div>
        ))}

        {/* Follow-up */}
        {(afterText || followUp) && (
          <div style={{ paddingTop:2 }}>
            {afterText && <NarrativeBlock text={afterText} />}
            {followUp  && <FollowUpText   text={followUp}  />}
          </div>
        )}

        {/* Follow-up chips (Image 1 bottom row) */}
        <div style={{ display:'flex', gap:6, flexWrap:'wrap', paddingBottom:2 }}>
          {followChips.map(({ label, icon }) => (
            <div key={label} style={{
              display:'inline-flex', alignItems:'center', gap:5,
              padding:'6px 13px', borderRadius:20, cursor:'pointer',
              background:R.accentSoft, border:`1px solid ${R.accentBdr}`,
              fontSize:12.5, color:'rgba(180,210,255,0.85)',
            }}>
              <span>{icon}</span><span>{label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right sidebar */}
      <div style={{ width:196, flexShrink:0, display:'flex', flexDirection:'column', gap:6 }}>
        <SidebarCards />
      </div>
    </div>
  );
}

// ── Helpers exclusivos do BibleStudyPresentation ──────────────────────────────

function BibleVerseContent({ content }) {
  if (!content) return null;
  const lines = content.split('\n');
  const quoteLines = [];
  const otherLines = [];

  for (const line of lines) {
    const t = line.trim();
    if (t.startsWith('>')) {
      quoteLines.push(t.replace(/^>\s*/, ''));
    } else if (t) {
      otherLines.push(t);
    }
  }

  const verseText = quoteLines.length > 0 ? quoteLines.join(' ') : stripBold(content);
  const source = otherLines.find(l => /^[—–-]/.test(l));

  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{
        fontSize: 44,
        color: 'rgba(245,158,11,0.28)',
        lineHeight: 1,
        marginBottom: -6,
        fontFamily: 'Georgia, "Times New Roman", serif',
        userSelect: 'none',
      }}>"</div>
      <div style={{
        fontSize: PT.fLg,
        color: PT.text,
        lineHeight: 1.72,
        fontStyle: 'italic',
        padding: '6px 0 6px 14px',
        borderLeft: '3px solid rgba(245,158,11,0.45)',
      }}>
        {stripBold(verseText)}
      </div>
      {source && (
        <div style={{
          fontSize: PT.fSm,
          color: 'rgba(245,158,11,0.55)',
          marginTop: 8,
          textAlign: 'right',
        }}>
          {source}
        </div>
      )}
    </div>
  );
}

function BibleStudyCard({ reference, sections, aplicSection, actions }) {
  return (
    <div style={{
      background: 'linear-gradient(145deg, #0e1628 0%, #0c1220 100%)',
      border: '1px solid rgba(245,158,11,0.22)',
      borderRadius: 16,
      overflow: 'hidden',
      boxShadow: '0 4px 24px rgba(0,0,0,0.35)',
      marginBottom: 4,
    }}>
      {/* Categoria */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '15px 20px 11px',
        borderBottom: '1px solid rgba(245,158,11,0.13)',
      }}>
        <span style={{ fontSize: 15, lineHeight: 1 }}>📖</span>
        <span style={{
          fontSize: 11, fontWeight: 700, color: PT.gold,
          textTransform: 'uppercase', letterSpacing: '0.11em',
        }}>
          Estudo Bíblico
        </span>
        <div style={{
          marginLeft: 'auto',
          width: 3, height: 18, borderRadius: 2,
          background: PT.gold, opacity: 0.65,
        }} />
      </div>

      {/* Referência principal */}
      <div style={{ padding: '16px 20px 0' }}>
        <div style={{
          fontSize: PT.fXl,
          fontWeight: 800,
          color: PT.gold,
          lineHeight: 1.25,
          letterSpacing: '-0.01em',
          marginBottom: 12,
        }}>
          {reference}
        </div>
        <div style={{
          height: 1,
          background: 'linear-gradient(90deg, rgba(245,158,11,0.38) 0%, transparent 80%)',
          marginBottom: 16,
        }} />
      </div>

      {/* Conteúdo das seções (versículos) */}
      <div style={{ padding: '0 20px' }}>
        {sections.map((s, i) => (
          <div key={i} style={{ marginBottom: i < sections.length - 1 ? 18 : 0 }}>
            {s.title && s.title !== reference && (
              <div style={{
                fontSize: PT.fXs, fontWeight: 700, color: PT.gold,
                textTransform: 'uppercase', letterSpacing: '0.09em',
                marginBottom: 10, opacity: 0.7,
              }}>
                {s.title}
              </div>
            )}
            <BibleVerseContent content={s.content} />
          </div>
        ))}

        {aplicSection && (
          <HighlightBox type="tip" title={aplicSection.title}>
            {aplicSection.content && <SectionContent content={aplicSection.content} />}
          </HighlightBox>
        )}
      </div>

      {/* Badge Bíblia Sagrada + ações */}
      <div style={{ padding: '14px 20px 16px' }}>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '5px 14px',
          background: 'rgba(245,158,11,0.09)',
          border: '1px solid rgba(245,158,11,0.22)',
          borderRadius: 20,
          fontSize: 11, fontWeight: 700,
          color: PT.gold, letterSpacing: '0.08em',
          marginBottom: 12,
        }}>
          ✦ BÍBLIA SAGRADA
        </div>
        <ActionBar actions={actions} />
      </div>
    </div>
  );
}

// ── 2. BibleStudyPresentation ─────────────────────────────────────────────────
export function BibleStudyPresentation({ content, title, meta }) {
  const { intro, sections, afterText, followUp } = parseNarrativeBlocks(content);

  const reflexSection = sections.find(s => sectionIs(s.title, 'reflex', 'medita', 'pensar', 'pergun'));
  const aplicSection  = sections.find(s => sectionIs(s.title, 'aplica', 'prática', 'pratica', 'vida'));
  const cardSections  = sections.filter(s => s !== reflexSection && s !== aplicSection);

  const reference = title || meta?.title || cardSections[0]?.title || 'Estudo Bíblico';

  const actions = [
    { id: 'save', label: 'Salvar estudo' },
    { id: 'continue', label: 'Continuar' },
    { id: 'copy', label: 'Copiar' },
  ];

  const hasCard = sections.length > 0;

  return (
    <>
      {/* 1. Introdução antes do card */}
      {intro && hasCard && <NarrativeBlock text={intro} />}

      {/* 2. Card bíblico principal */}
      {hasCard ? (
        <BibleStudyCard
          reference={reference}
          sections={cardSections}
          aplicSection={aplicSection}
          actions={actions}
        />
      ) : (
        <PresentationCard title="Estudo Bíblico" icon="✦" variant="spiritual" actions={<ActionBar actions={actions} />}>
          <IntroText text={intro || content} limit={700} />
        </PresentationCard>
      )}

      {/* 3. Reflexão fora do card (quando é uma seção nomeada) */}
      {reflexSection && (
        <NarrativeBlock
          text={stripBold(reflexSection.content)}
          style={{ fontStyle: 'italic' }}
        />
      )}

      {/* 4. Texto posterior + pergunta de continuidade */}
      {afterText && <NarrativeBlock text={afterText} />}
      {followUp && <FollowUpText text={followUp} />}
    </>
  );
}

// ── 3. StudyNotePresentation ──────────────────────────────────────────────────
export function StudyNotePresentation({ content, title, meta }) {
  const { intro, sections, afterText, followUp } = parseNarrativeBlocks(content);

  const exemploSection = sections.find(s => sectionIs(s.title, 'exemplo', 'prática', 'pratica', 'aplica'));
  const resumoSection  = sections.find(s => sectionIs(s.title, 'resumo', 'síntese', 'sintese', 'conclus'));
  const questSection   = sections.find(s => sectionIs(s.title, 'questão', 'questao', 'pergun', 'reflex', 'fixaç'));
  const mainSections   = sections.filter(s => s !== exemploSection && s !== resumoSection && s !== questSection);

  const actions = [
    { id: 'save', label: 'Salvar nota' },
    { id: 'copy', label: 'Copiar' },
  ];

  const hasCard = sections.length > 0;

  return (
    <>
      {intro && hasCard && <NarrativeBlock text={intro} />}

      <PresentationCard
        title={title || meta?.title || 'Estudo'}
        icon="◈"
        variant="default"
        actions={<ActionBar actions={actions} />}
      >
        {hasCard ? (
          <>
            {mainSections.map((s, i) => (
              <PresentationSection key={i} title={s.title} accent={PT.accent}>
                <SectionContent content={s.content} />
              </PresentationSection>
            ))}

            {exemploSection && (
              <HighlightBox type="info" title={exemploSection.title}>
                <SectionContent content={exemploSection.content} />
              </HighlightBox>
            )}

            {resumoSection && (
              <HighlightBox type="note" title={resumoSection.title} content={stripBold(resumoSection.content)} />
            )}

            {questSection && (
              <HighlightBox type="tip" title={questSection.title} content={stripBold(questSection.content)} />
            )}
          </>
        ) : (
          <IntroText text={intro || content} limit={700} />
        )}
      </PresentationCard>

      {afterText && <NarrativeBlock text={afterText} />}
      {followUp && <FollowUpText text={followUp} />}
    </>
  );
}

// ── 4. StepByStepPresentation ─────────────────────────────────────────────────
export function StepByStepPresentation({ content, title, meta }) {
  const { intro, sections, afterText, followUp } = parseNarrativeBlocks(content);

  const cuidadoSection  = sections.find(s => sectionIs(s.title, 'cuidado', 'atenç', 'aviso', 'warning', 'erro'));
  const resultSection   = sections.find(s => sectionIs(s.title, 'result', 'espera', 'final'));
  const stepSections    = sections.filter(s => s !== cuidadoSection && s !== resultSection);

  let allSteps = null;
  if (stepSections.length > 0) {
    const combined = stepSections.map(s => s.content).join('\n');
    allSteps = extractSteps(combined);
  }
  if (!allSteps) allSteps = extractSteps(intro || content);

  const actions = [
    { id: 'copy', label: 'Copiar passos' },
    { id: 'save', label: 'Salvar' },
  ];

  const hasCard = sections.length > 0;

  return (
    <>
      {intro && hasCard && <NarrativeBlock text={intro} />}

      <PresentationCard
        title={title || meta?.title || 'Tutorial'}
        icon="→"
        variant="default"
        actions={<ActionBar actions={actions} />}
      >
        {hasCard ? (
          <>
            {allSteps ? (
              <PresentationSection title="Passos" accent={PT.accent}>
                <StepList steps={allSteps} />
              </PresentationSection>
            ) : (
              sections.map((s, i) => (
                s !== cuidadoSection && s !== resultSection && (
                  <PresentationSection key={i} title={s.title} accent={PT.accent}>
                    <SectionContent content={s.content} />
                  </PresentationSection>
                )
              ))
            )}

            {cuidadoSection && (
              <HighlightBox type="warning" title={cuidadoSection.title} content={stripBold(cuidadoSection.content)} />
            )}

            {resultSection && (
              <HighlightBox type="success" title={resultSection.title} content={stripBold(resultSection.content)} />
            )}
          </>
        ) : (
          <IntroText text={content} limit={700} />
        )}
      </PresentationCard>

      {afterText && <NarrativeBlock text={afterText} />}
      {followUp && <FollowUpText text={followUp} />}
    </>
  );
}

// ── 5. ComparisonPresentation ─────────────────────────────────────────────────
function parseComparisonItems(sections) {
  return sections.map(s => {
    const lines = s.content.split('\n').filter(l => l.trim());
    const pros = [], cons = [], other = [];
    for (const line of lines) {
      if (/^[+✓✅]\s/.test(line.trim())) pros.push(line.replace(/^[+✓✅]\s+/, ''));
      else if (/^[-−✗❌]\s/.test(line.trim())) cons.push(line.replace(/^[-−✗❌]\s+/, ''));
      else other.push(line);
    }
    if (!pros.length && !cons.length) {
      const bullets = extractBullets(s.content);
      if (bullets) bullets.forEach(b => pros.push(b));
    }
    return { name: s.title, pros, cons };
  });
}

export function ComparisonPresentation({ content, title, meta }) {
  const { intro, sections, afterText, followUp } = parseNarrativeBlocks(content);

  const recSection   = sections.find(s => sectionIs(s.title, 'recomend', 'conclus', 'veredicto', 'melhor'));
  const compSections = sections.filter(s => s !== recSection);

  const items = compSections.length >= 2 ? parseComparisonItems(compSections) : [];

  const actions = [
    { id: 'copy', label: 'Copiar' },
    { id: 'save', label: 'Salvar' },
  ];

  const hasCard = sections.length > 0;

  return (
    <>
      {intro && hasCard && <NarrativeBlock text={intro} />}

      <PresentationCard
        title={title || meta?.title || 'Comparação'}
        icon="⊞"
        variant="default"
        actions={<ActionBar actions={actions} />}
      >
        {hasCard ? (
          <>
            {items.length >= 2 ? (
              <ComparisonBlock
                items={items}
                recommendation={recSection ? stripBold(recSection.content) : null}
              />
            ) : (
              sections.map((s, i) => (
                <PresentationSection key={i} title={s.title} accent={PT.accent}>
                  <SectionContent content={s.content} />
                </PresentationSection>
              ))
            )}
          </>
        ) : (
          <IntroText text={content} limit={700} />
        )}
      </PresentationCard>

      {afterText && <NarrativeBlock text={afterText} />}
      {followUp && <FollowUpText text={followUp} />}
    </>
  );
}

// ── 6. SimpleConversationPresentation ─────────────────────────────────────────
// Sem card — renderiza texto limpo através do motor de apresentação existente.
export function SimpleConversationPresentation({ content, AthenaPresentationEngine, cognitiveProfile }) {
  return (
    <div style={{ fontSize: PT.fBase, lineHeight: 1.72 }}>
      <AthenaPresentationEngine content={content} cognitiveProfile={cognitiveProfile} />
    </div>
  );
}

// ── Generic fallback: layout_type desconhecido ────────────────────────────────
export function GenericPresentation({ content, title, meta }) {
  const { intro, sections, afterText, followUp } = parseNarrativeBlocks(content);
  const actions = [{ id: 'copy', label: 'Copiar' }, { id: 'save', label: 'Salvar' }];
  const hasCard = sections.length > 0;

  return (
    <>
      {intro && hasCard && <NarrativeBlock text={intro} />}

      <PresentationCard
        title={title || meta?.title}
        icon="◈"
        actions={<ActionBar actions={actions} />}
      >
        {hasCard ? (
          sections.map((s, i) => (
            <PresentationSection key={i} title={s.title} accent={PT.accent}>
              <SectionContent content={s.content} />
            </PresentationSection>
          ))
        ) : (
          <IntroText text={intro || content} limit={700} />
        )}
      </PresentationCard>

      {afterText && <NarrativeBlock text={afterText} />}
      {followUp && <FollowUpText text={followUp} />}
    </>
  );
}
